export class Input {
  constructor(canvas) {
    this.keys = new Set();
    this.leftHeld = false; this.rightHeld = false; this.jumpQueued = false;
    this.jumpHeld = false;
    this.downHeld = false;
    this.interactQueued = false;
    this.interactHeld = false;
    this.dialogueQueued = false;
    this.dialogueHeld = false;
    this.levitateQueued = false;
    this.levitateHeld = false;
    this.hasKeyboard = false;
    this.runHeld = false;
    this.rotateHeld = false; // builder rotate key (X)

    // Mouse tracking (canvas-relative coordinates)
    this.mouseX = 0;
    this.mouseY = 0;
    this.mouseClickQueued = false;
    this.mousePresent = false; // true when mouse has moved over canvas
    this.layout = 'ZQSD';
    this._leftKeys = new Set();
    this._rightKeys = new Set();
    this._jumpKeys = new Set();
    this._interactKeys = new Set();
    this._dialogueKeys = new Set();
    this._runKeys = new Set();
    this._rotateKeys = new Set();
    this._downKeys = new Set();
    this._levitateKeys = new Set();

    // Relative-touch control state (mobile)
    this._isTouchEnv = !!(window.matchMedia && window.matchMedia('(hover:none) and (pointer:coarse)').matches);
    this._playerScreenRect = { x: 0, y: 0, w: 0, h: 0 };
    this._primaryPointerId = null;
    this._secondaryPointers = new Set(); // any additional active fingers
    this._lastPointer = null; // { x, y } in CSS pixels
    // Tap/double-tap detection (primary)
    this._primaryDownTS = 0;
    this._primaryDownPos = null;
    this._lastTapTS = 0;
    this._lastTapPos = null;
    this._prevUpHeld = false;
    this._prevDownHeld = false;

    // Listener lifecycle
    this._ac = new AbortController();
    const signal = this._ac.signal;

    const down = (e) => {
      const k = e.key.toLowerCase();
      this.hasKeyboard = true;
      if (k === 'arrowleft' || this._leftKeys.has(k)) this.leftHeld = true;
      if (k === 'arrowright' || this._rightKeys.has(k)) this.rightHeld = true;
      if (k === 'arrowup' || this._jumpKeys.has(k)) { this.jumpQueued = true; this.jumpHeld = true; }
      if (k === 'arrowdown' || this._downKeys.has(k)) this.downHeld = true;
      if (k === 'c' || this._levitateKeys.has(k)) { this.levitateQueued = true; this.levitateHeld = true; }
      if (this._interactKeys.has(k)) { this.interactQueued = true; this.interactHeld = true; } // interact/pick up
      if (this._dialogueKeys.has(k)) { this.dialogueQueued = true; this.dialogueHeld = true; }
      if (k === 'shift' || this._runKeys.has(k)) this.runHeld = true;
      if (this._rotateKeys.has(k) || k === 'x') this.rotateHeld = true;
    };
    const up = (e) => {
      const k = e.key.toLowerCase();
      if (k === 'arrowleft' || this._leftKeys.has(k)) this.leftHeld = false;
      if (k === 'arrowright' || this._rightKeys.has(k)) this.rightHeld = false;
      if (k === 'arrowup' || this._jumpKeys.has(k)) this.jumpHeld = false;
      if (k === 'arrowdown' || this._downKeys.has(k)) this.downHeld = false;
      if (k === 'c' || this._levitateKeys.has(k)) this.levitateHeld = false;
      if (this._interactKeys.has(k)) this.interactHeld = false;
      if (this._dialogueKeys.has(k)) this.dialogueHeld = false;
      if (k === 'shift' || this._runKeys.has(k)) this.runHeld = false;
      if (this._rotateKeys.has(k) || k === 'x') this.rotateHeld = false;
    };
    window.addEventListener('keydown', down, { signal });
    window.addEventListener('keyup', up, { signal });

    // Mouse tracking for builder tool
    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
      this.mousePresent = true;
    }, { signal, passive: true });

    canvas.addEventListener('mouseleave', () => {
      this.mousePresent = false;
    }, { signal, passive: true });

    canvas.addEventListener('click', (e) => {
      if (this._isTouchEnv) return; // Skip on touch devices
      this.mouseClickQueued = true;
      e.preventDefault();
    }, { signal });

    const left = document.getElementById('left');
    const right = document.getElementById('right');
    const jump = document.getElementById('jump');
    const interact = document.getElementById('interact');

    const press = (setter, val) => (e) => { e.preventDefault(); setter(val); };
    const bindHold = (el, setter) => {
      if (!el) return;
      el.addEventListener('pointerdown', press(setter, true), { signal });
      const stop = press(setter, false);
      el.addEventListener('pointerup', stop, { signal });
      el.addEventListener('pointerleave', stop, { signal });
      el.addEventListener('pointercancel', stop, { signal });
    };
    bindHold(left,  (v)=> this.leftHeld = v);
    bindHold(right, (v)=> this.rightHeld = v);
    // Jump: queue on press and treat as a hold while pressed
    if (jump) {
      jump.addEventListener('pointerdown', (e)=>{ e.preventDefault(); this.jumpQueued = true; this.jumpHeld = true; }, { signal });
      jump.addEventListener('pointerup',   (e)=>{ e.preventDefault(); this.jumpHeld = false; }, { signal });
      jump.addEventListener('pointerleave',(e)=>{ e.preventDefault(); this.jumpHeld = false; }, { signal });
      jump.addEventListener('pointercancel',(e)=>{ e.preventDefault(); this.jumpHeld = false; }, { signal });
    }
    if (interact) {
      interact.addEventListener('pointerdown', (e)=>{ e.preventDefault(); this.interactQueued = true; this.interactHeld = true; this.dialogueQueued = true; this.dialogueHeld = true; }, { signal });
      const stopInteract = (e)=>{ e.preventDefault(); this.interactHeld = false; this.dialogueHeld = false; };
      interact.addEventListener('pointerup', stopInteract, { signal });
      interact.addEventListener('pointerleave', stopInteract, { signal });
      interact.addEventListener('pointercancel', stopInteract, { signal });
    }

    // Mobile: relative-touch controls based on finger vs player position
    const ignoreIfControls = (t) => !!(t && t.closest && t.closest('.controls'));
    // Joystick (mobile)
    const joy = document.getElementById('joystick');
    const joyStick = joy ? joy.querySelector('.stick') : null;
    this._joyPtrId = null; this._joyCenter = null; this._joyStickEl = joyStick;
    const joyDead = 12; const joyMax = 46; // pixels in the control
    const joyUpdate = (x, y)=>{
      if (!this._isTouchEnv || !joy) return;
      const r = joy.getBoundingClientRect();
      const cx = r.left + r.width/2, cy = r.top + r.height/2;
      const dx = x - cx, dy = y - cy;
      const dist = Math.hypot(dx, dy);
      const ndx = Math.abs(dx) < joyDead ? 0 : dx;
      const ndy = Math.abs(dy) < joyDead ? 0 : dy;
      // Update held states
      const prevUp = this._prevUpHeld;
      this.leftHeld = (ndx < -joyDead);
      this.rightHeld = (ndx > joyDead);
      const upHeld = (ndy < -joyDead);
      if (upHeld && !prevUp) this.jumpQueued = true;
      this.jumpHeld = upHeld;
      this._prevUpHeld = upHeld;
      // Move the stick visual within bounds
      if (this._joyStickEl) {
        const k = Math.min(1, dist / joyMax);
        const ox = (dx / (dist || 1)) * joyMax * k;
        const oy = (dy / (dist || 1)) * joyMax * k;
        this._joyStickEl.style.transform = `translate(${Math.round(ox)}px, ${Math.round(oy)}px)`;
        this._joyStickEl.style.left = '50%'; this._joyStickEl.style.top = '50%';
      }
    };
    if (joy) {
      joy.addEventListener('pointerdown', (e)=>{
        if (!this._isTouchEnv) return;
        this._joyPtrId = e.pointerId;
        joy.setPointerCapture?.(e.pointerId);
        joyUpdate(e.clientX, e.clientY);
        e.preventDefault();
        e.stopPropagation();
        joy.classList.add('active');
      }, { signal });
      joy.addEventListener('pointermove', (e)=>{
        if (!this._isTouchEnv) return;
        if (this._joyPtrId == null || e.pointerId !== this._joyPtrId) return;
        joyUpdate(e.clientX, e.clientY);
      }, { signal, passive: true });
      const joyEnd = (e)=>{
        if (!this._isTouchEnv) return;
        if (e.pointerId !== this._joyPtrId) return;
        this._joyPtrId = null;
        // Reset states
        this.leftHeld = false; this.rightHeld = false; this.jumpHeld = false;
        this._prevUpHeld = false;
        if (this._joyStickEl) this._joyStickEl.style.transform = 'translate(-50%, -50%)';
        joy.classList.remove('active');
      };
      joy.addEventListener('pointerup', joyEnd, { signal, passive: true });
      joy.addEventListener('pointercancel', joyEnd, { signal, passive: true });
    }
    const recomputeFromPointer = () => {
      if (!this._isTouchEnv || !this._lastPointer) return;
      const r = this._playerScreenRect;
      const cx = r.x + r.w / 2;
      const cy = r.y + r.h / 2;
      const dx = this._lastPointer.x - cx;
      const dy = this._lastPointer.y - cy;
      const dead = Math.max(14, Math.min(28, Math.min(r.w, r.h) * 0.25));

      // Horizontal movement
      this.leftHeld = (dx < -dead);
      this.rightHeld = (dx > dead);

      // Vertical actions: Up = jump; Down no longer maps to interact
      const upHeld = (dy < -dead);
      if (upHeld && !this._prevUpHeld) this.jumpQueued = true;
      this.jumpHeld = upHeld;
      this._prevUpHeld = upHeld;
    };
    const clearRelative = () => {
      this._primaryPointerId = null;
      this._secondaryPointers.clear();
      this._lastPointer = null;
      this.leftHeld = false; this.rightHeld = false;
      this.jumpHeld = false; this.interactHeld = false;
      this.dialogueHeld = false; this.dialogueQueued = false;
      this.downHeld = false; this.levitateHeld = false;
      this._prevUpHeld = false; this._prevDownHeld = false;
    };
    canvas.addEventListener('pointerdown', (e) => {
      if (!this._isTouchEnv) return;
      if (ignoreIfControls(e.target)) return;
      if (this._primaryPointerId == null) {
        // First finger: begin potential movement/jump control; tap recognized on release
        this._primaryPointerId = e.pointerId;
        this._primaryDownTS = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        this._primaryDownPos = { x: e.clientX, y: e.clientY };
        this._lastPointer = { x: e.clientX, y: e.clientY };
        recomputeFromPointer();
        e.preventDefault();
      } else {
        // Second (or more) finger triggers interact while held
        if (!this._secondaryPointers.has(e.pointerId)) {
          this._secondaryPointers.add(e.pointerId);
          this.interactQueued = true;
          this.interactHeld = true;
          this.dialogueQueued = true;
          this.dialogueHeld = true;
        }
        e.preventDefault();
      }
    }, { signal });
    window.addEventListener('pointermove', (e) => {
      if (!this._isTouchEnv) return;
      if (this._primaryPointerId != null && e.pointerId === this._primaryPointerId) {
        this._lastPointer = { x: e.clientX, y: e.clientY };
        recomputeFromPointer();
      }
    }, { signal, passive: true });
    const endRel = (e) => {
      if (!this._isTouchEnv) return;
      if (e.pointerId === this._primaryPointerId) {
        // Tap/double-tap detection: double-tap => jump, single tap => interact
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const downTS = this._primaryDownTS || now;
        const dur = now - downTS;
        const downPos = this._primaryDownPos || { x: e.clientX, y: e.clientY };
        const dx = e.clientX - downPos.x;
        const dy = e.clientY - downPos.y;
        const dist2 = dx*dx + dy*dy;
        const isTap = dur <= 220 && dist2 <= (18*18);
        if (isTap) {
          const dt = now - (this._lastTapTS || 0);
          const nearPrev = this._lastTapPos ? ((Math.hypot(this._lastTapPos.x - e.clientX, this._lastTapPos.y - e.clientY)) <= 28) : false;
          if (dt > 0 && dt <= 350 && nearPrev) {
            this.jumpQueued = true;
            this._lastTapTS = 0; this._lastTapPos = null; // consume combo
          } else {
            // Single tap anywhere => interact
            this.interactQueued = true;
            this.dialogueQueued = true;
            this._lastTapTS = now; this._lastTapPos = { x: e.clientX, y: e.clientY };
          }
        }
        // End primary gesture
        clearRelative();
      } else if (this._secondaryPointers.has(e.pointerId)) {
        this._secondaryPointers.delete(e.pointerId);
        this.interactHeld = this._secondaryPointers.size > 0;
        this.dialogueHeld = this._secondaryPointers.size > 0;
      }
    };
    window.addEventListener('pointerup', endRel, { signal, passive: true });
    window.addEventListener('pointercancel', endRel, { signal, passive: true });
    // Allow focusing externally (e.g., via tap overlay) without forcing focus immediately
    canvas.tabIndex = 0;

    // Initialize layout (possibly overridden by main.js after construction)
    this.setLayout(this.layout);

    // Expose recompute function for external rect updates
    this._recomputeFromPointer = recomputeFromPointer;

    // Expose disposer
    this.dispose = () => { try { this._ac.abort(); } catch {} };
  }

  consumeJump() { const jq = this.jumpQueued; this.jumpQueued = false; return jq; }
  consumeInteract() { const iq = this.interactQueued; this.interactQueued = false; return iq; }
  consumeDialogue() { const dq = this.dialogueQueued; this.dialogueQueued = false; return dq; }
  consumeLevitate() { const lq = this.levitateQueued; this.levitateQueued = false; return lq; }
  consumeMouseClick() { const mc = this.mouseClickQueued; this.mouseClickQueued = false; return mc; }

  setLayout(name) {
    const n = (name || '').toUpperCase();
    this.layout = ['WASD','ZQSD'].includes(n) ? n : 'ZQSD';
    const left = new Set();
    const right = new Set();
    const jump = new Set([' ', 'arrowup']); // keep Space and ArrowUp usable
    const interact = new Set(['arrowdown']); // Down arrow always interacts
    const dialogue = new Set(['e']);
    const run = new Set([]); // Run via Shift; keep letters empty by default
    const rotate = new Set(['x']); // X rotates builder planks
    const down = new Set(['arrowdown']);
    const levitate = new Set(['c']);

    // Always allow arrows in handlers; here we just add letters.
    if (this.layout === 'WASD') {
      left.add('a'); right.add('d'); jump.add('w');
      interact.add('s'); down.add('s');
    } else if (this.layout === 'ZQSD') {
      left.add('q'); right.add('d'); jump.add('z');
      interact.add('s'); down.add('s');
    }

    this._leftKeys = left;
    this._rightKeys = right;
    this._jumpKeys = jump;
    this._interactKeys = interact;
    this._dialogueKeys = dialogue;
    this._runKeys = run;
    this._rotateKeys = rotate;
    this._downKeys = down;
    this._levitateKeys = levitate;
  }

  // Called each frame with the player's current screen-space rect (CSS pixels)
  setPlayerScreenRect(x, y, w, h) {
    this._playerScreenRect = { x, y, w, h };
    if (this._isTouchEnv && this._primaryPointerId != null && typeof this._recomputeFromPointer === 'function') {
      this._recomputeFromPointer();
    }
  }
}
